import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { journeys as journeysApi, segments as segmentsApi, products as productsApi } from '../../../apiService/blustreamApi';
import { fetchAll } from './useJourneyData';
import { queryKeys } from './queries';

export function useJourneyMutation() {
  const qc = useQueryClient();
  return useMutation(
    async (vars) => {
      let segmentId = vars.segment.segmentId;
      if (vars.segment.changed) {
        if (segmentId) await segmentsApi.update(segmentId, vars.segment.body);
        else { const created = await segmentsApi.create(vars.segment.body); segmentId = created.segment_id; }
      }
      const body = { ...vars.journeyBody };
      if ((vars.isCreate || vars.segment.changed) && segmentId != null) body.segment_id = segmentId;
      const saved = vars.isCreate ? await journeysApi.create(body) : await journeysApi.update(vars.journeyId, body);
      const journeyId = vars.isCreate ? saved.journey_id : vars.journeyId;
      await Promise.all([
        ...vars.labelDiff.added.map((u) => journeysApi.attachLabel(journeyId, u)),
        ...vars.labelDiff.removed.map((u) => journeysApi.detachLabel(journeyId, u)),
      ]);
      return { journeyId };
    },
    {
      onSuccess: async () => {
        // The journey-detail query rebuilds its journey set from the list via ensureQueryData,
        // which serves cached data WITHOUT refetching. So a newly-created journey only shows up
        // after the redirect if we DROP the cached list (not merely invalidate it). Use the real
        // key — queryKeys.journeyList (['journeys','list']); the prior ['journeyList'] matched
        // nothing, so the list never refreshed and the new journey 404'd until a hard refresh.
        qc.removeQueries(queryKeys.journeyList);
        qc.removeQueries(['journeyDetail']);
        await qc.invalidateQueries(queryKeys.journeyList);
      },
    },
  );
}

// Hard-delete a journey (DELETE /journeys/:id), then drop the cached list + details so
// the gallery/detail views refresh. Mirrors useJourneyMutation's onSuccess.
export function useDeleteJourney() {
  const qc = useQueryClient();
  return useMutation(
    (journeyId) => journeysApi.remove(journeyId),
    {
      onSuccess: async () => {
        qc.removeQueries(queryKeys.journeyList);
        qc.removeQueries(['journeyDetail']);
        await qc.invalidateQueries(queryKeys.journeyList);
      },
    },
  );
}

export function useProductOptions() {
  return useQuery(['productOptions'], async ({ signal }) => {
    const all = await fetchAll((page) => productsApi.list({ page, size: 200, signal }));
    return all.map((p) => ({ value: String(p.product_id), label: p.name }));
  });
}

// Single integration point for a future "count matching contacts" endpoint.
export function useSegmentMatchCount() {
  return { status: 'unavailable', count: null };
}
